import type { FC } from 'react';
import { Fragment, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

import { getContrastColor } from 'src/helper/getContrastColor';
import DoughnutSkeleton from 'src/components/followUp/common/BaseWidgets/DoughnutChart/DoughnutSkeleton';

interface DoughnutProps {
  data: Array<{ label: string; absoluteValue: number; relativeValue: number }>;
  labels: Array<{
    name: string;
    color: string;
    comparisonColor?: string;
  }>;
  showToolTip: boolean;
  radius: number;
  comparison?: boolean;
}

const renderDoughnut = ({
  data,
  labels,
  showToolTip,
  radius,
  comparison = false,
}: DoughnutProps) =>
  data.length ? (
    data.map((slice, index) => {
      const label = labels.find((label) => label.name === slice.label);
      if (!label || slice.relativeValue == 0) return null;

      const color =
        comparison && label.comparisonColor
          ? label.comparisonColor
          : label.color;

      // Calculate rotation and midpoint angle
      const startAngle = data
        .slice(0, index)
        .reduce((prev, curr) => prev + (curr.relativeValue / 100) * 360, 0);
      const endAngle = startAngle + (slice.relativeValue / 100) * 360;
      const midAngle = (startAngle + endAngle) / 2;

      // Convert polar to Cartesian coordinates for text positioning
      const circumference = 2 * radius * Math.PI;
      const textX = 50 + radius * Math.cos((midAngle * Math.PI) / 180); // Adjust for SVG's top-left origin
      const textY = 50 + radius * Math.sin((midAngle * Math.PI) / 180);

      return (
        <Fragment key={slice.label}>
          <svg
            viewBox="0 0 100 100"
            width="100%"
            height="100%"
            fill="none"
            style={{
              transform: `rotate(${startAngle}deg)`,
              position: 'absolute',
              transition: 'transform 0.5s ease-out',
            }}
          >
            {/* Circle segment */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              stroke={color}
              strokeWidth={15}
              strokeDasharray={`${(slice.relativeValue / 100) * circumference} ${circumference}`}
            />
          </svg>
          <svg
            viewBox="0 0 100 100"
            width="100%"
            height="100%"
            fill="none"
            style={{
              position: 'absolute',
            }}
          >
            Text for relative value
            <Tooltip title={`n=${slice.absoluteValue}`} open={showToolTip}>
              <text
                x={textX}
                y={textY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={getContrastColor(color)}
                fontSize="4"
                fontWeight="bold"
              >
                {Math.round(slice.relativeValue)}%
              </text>
            </Tooltip>
          </svg>
        </Fragment>
      );
    })
  ) : (
    <DoughnutSkeleton radius={radius} circumference={2 * radius * Math.PI} />
  );

interface DoughnutChartProps {
  data: Array<{ label: string; absoluteValue: number; relativeValue: number }>;
  comparisonData?: Array<{
    label: string;
    absoluteValue: number;
    relativeValue: number;
  }>;
  labels: Array<{
    name: string;
    color: string;
    comparisonColor?: string;
  }>;
}

const DoughnutChart: FC<DoughnutChartProps> = ({
  data,
  comparisonData = [],
  labels,
}) => {
  const [showToolTip, setShowToolTip] = useState(false);
  return (
    <Box
      sx={{
        p: 2,
        flexGrow: 1,
        width: '100%',
        display: 'flex',
      }}
    >
      <Box
        onMouseEnter={() => setShowToolTip(true)}
        onMouseLeave={() => setShowToolTip(false)}
        sx={{
          width: '100%',
          height: 280,
          position: 'relative',
        }}
      >
        {renderDoughnut({
          data,
          labels,
          showToolTip,
          radius: 42,
        })}
        {comparisonData.length
          ? renderDoughnut({
              data: comparisonData,
              labels,
              showToolTip,
              radius: 26,
              comparison: true,
            })
          : null}
      </Box>
    </Box>
  );
};

export default DoughnutChart;
